@use "styles" as *;
@use "sass:map";

$block: me-form-control;

@function line-height($height) {
  @return calc($height - 2 * $form-control-border-width);
}

@mixin text($element) {
  .#{$block} {
    &__form-element {
      display: flex;
      align-items: center;
      gap: space(2);
      border: $form-control-border-width solid $color-gray-300;
      box-shadow: 0 0 0 21474836px $color-background inset;

      &:focus-within {
        border-color: $color-primary;
        box-shadow: $box-shadow-primary;
      }

      & > *:first-child {
        flex: 1;
      }

      #{$element} {
        color: $color-text;
        appearance: none;
        background: transparent;
        border: none;
        outline: none;
      }
    }
  }

  &.#{$block}--valid .#{$block} {
    &__form-element {
      border-color: $color-success;
      box-shadow: $box-shadow-success;
    }
  }

  &.#{$block}--invalid .#{$block} {
    &__form-element {
      border-color: $color-danger;
      box-shadow: $box-shadow-danger;
    }
  }
}

.#{$block} {
  display: grid;
  grid:
    "label"
    "form-element"
    "description"
    "errors"
    / 1fr auto;
  font-size: $font-size-r;

  &__label {
    grid-area: label;
    user-select: none;
    margin-bottom: space(2);

    &:empty {
      display: none;
    }
  }

  &__required-asterisk {
    color: $color-danger;
  }

  &__form-element {
    grid-area: form-element;
  }

  &__description, &__errors {
    margin-top: space(2);
    font-size: $font-size-s;
  }

  &__description {
    grid-area: description;
  }

  &__errors {
    grid-area: errors;
  }

  &--valid {
    .#{$block} {
      &__description,
      &__errors {
        color: $color-success;
      }
    }
  }

  &--invalid {
    .#{$block} {
      &__description,
      &__errors {
        color: $color-danger;
      }
    }

    .#{$block}--radio .#{$block}__form-element input {
      border-color: $color-danger;
    }
  }

  &--control {
    .#{$block} {
      &__form-element {
        display: flex;
        flex-direction: column;
        gap: space(2);
      }
    }
  }

  &--checkbox, &--radio {
    align-items: center;
    grid:
      "form-element . label"
      "description description description"
      "errors errors errors"
      / auto space(2) 1fr;

    .#{$block}__label {
      margin-bottom: unset;
    }

    .#{$block}__form-element {
      display: inline-flex;

      input {
        height: $form-control-checkbox-size;
        width: $form-control-checkbox-size;
        border: $form-control-border-width solid $color-gray-300;
        appearance: none;

        &:checked {
          background: $color-primary;
          border-color: $color-primary;
        }
      }
    }
  }

  &--me-card-radio {
    .#{$block}__form-element {
      display: flex;
      flex-direction: column;
      gap: space(5);
    }
  }

  &--radio {
    .#{$block}__form-element input {
      border-radius: $form-control-radio-border-radius;
      position: relative;

      &:checked::before {
        position: absolute;
        inset: 50% 0 0 50%;
        transform: translate(-50%, -50%);
        display: block;
        content: "";
        background-color: white;
        width: $form-control-radio-bullet-size;
        height: $form-control-radio-bullet-size;
        border-radius: $form-control-radio-border-radius;
        overflow: hidden;
      }
    }
  }

  &--checkbox {
    .#{$block}__form-element input {
      border-radius: $form-control-checkbox-border-radius;

      &:checked::before {
        display: block;
        content: "";
        height: calc($form-control-checkbox-size / 2);
        width: calc($form-control-checkbox-size / 4);
        border-bottom: $form-control-checkbox-checkmark;
        border-right: $form-control-checkbox-checkmark;
        transform: translate(calc($form-control-checkbox-size / 4), 10%) rotate(45deg);
      }
    }

    &.#{$block}--invalid input {
      border-color: $color-danger;
    }
  }

  &--select {
    .#{$block} {
      &__form-element {
        position: relative;

        &:focus-within {
          box-shadow: $box-shadow-primary;
        }
      }

      &__icons {
        position: absolute;
        pointer-events: none;
        top: 0;
        right: space(4);
        bottom: 0;
        display: flex;
        align-items: center;
        color: $color-primary;
      }
    }

    select {
      appearance: none;
      outline: none;
      background-color: $color-background;
      width: 100%;
      border: $form-control-border-width solid $color-gray-300;
      border-radius: 0;
      line-height: line-height($form-control-select-height);
      padding: space(0, 12, 0, 4);
      color: inherit;
    }
  }

  &--text {
    @each $size, $size-options in $form-control-text-sizes {
      &.#{$block}--#{$size} .#{$block} {
        &__form-element {
          padding: 0 map.get($size-options, padding-h);

          input {
            line-height: line-height(map.get($size-options, height));
          }
        }
      }
    }

    @include text(input);

    .#{$block} {
      &__form-element {
        input {
          padding: 0;

          &::-webkit-search-cancel-button {
            filter: grayscale(1);
          }

          &::-ms-reveal,
          &::-ms-clear {
            display: none;
          }
        }

        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-text-fill-color: $color-text;
          transition: background-color 0s 2147483647s;
          box-shadow: 0 0 0 21474836px $color-background inset;
        }
      }

      &__icons {
        grid-area: icons;
        display: flex;
        gap: space(2);
        user-select: none;

        &:empty {
          display: none;
        }
      }

      &__eye-icons,
      &__validity-icons {
        display: grid;
        grid: "icon";
      }

      &__eye-icon,
      &__validity-icon {
        grid-area: icon;
        transition: color $transition-timing, opacity $transition-timing;
        opacity: 0;
      }

      &__eye-icon {
        cursor: pointer;
      }
    }

    &.#{$block}:not(.#{$block}--obfuscated) .#{$block}__eye-icon--slash,
    &.#{$block}--obfuscated .#{$block}__eye-icon--default,
    &.#{$block}--valid .#{$block}__validity-icon--valid,
    &.#{$block}--invalid .#{$block}__validity-icon--invalid {
      opacity: 1;
    }

    &.#{$block}--valid .#{$block} {
      &__icons {
        color: $color-success;
      }
    }

    &.#{$block}--invalid .#{$block} {
      &__icons {
        color: $color-danger;
      }
    }
  }

  &--textarea {
    @include text(textarea);
    .#{$block} {
      &__form-element {
        textarea {
          padding: space(3);
          resize: vertical;
        }
      }
    }
  }
}
